/**
 * List Tag Rules API Hooks
 * For Media Devoted | Created By Ciro Autiero
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
// Deep import — the stats-tab BARREL transitively re-enters '@/hooks' and
// circular-inits this module in test graphs.
import { VOLUUM_COLUMNS } from '@/features/smswiser-campaigns/components/SmswiserCampaignStatsTab/shared/constants';
import {
  STATS_DATE_RANGE_PRESETS,
  type StatsDateRangePreset,
} from '@/components/date-range';
import type { TagCompareTo, TagTrafficProvider } from '@trafficpulse/shared';
import api from '@/services/api';

/** Fields for condition builder – matches VOLUUM_COLUMNS (same as Lists stats tab) */
export const TAG_RULE_FIELDS = VOLUUM_COLUMNS.map((c) => c.key) as [string, ...string[]];

export const TAG_RULE_OPERATORS = ['gte', 'lte', 'gt', 'lt', 'eq'] as const;

/** Matches SMSWiser stats tab – same presets */
export const TAG_RULE_DATE_PRESETS = STATS_DATE_RANGE_PRESETS;
export type TagRuleDatePreset = StatsDateRangePreset;

export interface TagRuleCountryThreshold {
  countryKey: string;
  value: number;
  /** When set, this scope COMPARES instead of pinning `value`. */
  compareTo?: TagCompareTo;
}

/** Per-campaign threshold; beats a country override for that campaign. */
export interface TagRuleCampaignThreshold {
  provider: TagTrafficProvider;
  campaignId: string;
  value: number;
  /** When set, this scope COMPARES instead of pinning `value`. */
  compareTo?: TagCompareTo;
}

export interface TagRuleCondition {
  field: (typeof TAG_RULE_FIELDS)[number];
  operator: (typeof TAG_RULE_OPERATORS)[number];
  value: number;
  /** Compare against another metric instead of `value` (threshold = that
   *  metric over its own window x percent/100). Mutually exclusive with
   *  country/campaign thresholds. */
  compareTo?: TagCompareTo;
  /** Optional per-country thresholds; others use `value`. */
  countryThresholds?: TagRuleCountryThreshold[];
  /** Optional per-campaign thresholds; these beat countryThresholds. */
  campaignThresholds?: TagRuleCampaignThreshold[];
}

export interface ListTagRule {
  id: string;
  name: string;
  color: string | null;
  /** Drop rules only: emoji ("+" / "-") shown on the Drops tab. */
  emoji?: string | null;
  /** Drop + List rules: apply ONLY to single-sourced lists (drop rules: to
   *  drops sent to such a list). */
  singleSourceOnly?: boolean;
  /** Drop rules only: the drop's list must carry one of these system tags
   *  ("New" / "Unused"); empty = no gate. */
  listSystemTags?: string[];
  conditions: TagRuleCondition[];
  dateRangePreset: string;
  fromDate: string | null;
  toDate: string | null;
  toDateIsToday?: boolean;
  /** Hour rules only: unique visits an hour needs before the tag judges it. */
  minVolume?: number;
  enabled: boolean;
  orderIndex: number;
  groupKey: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateListTagRuleDto {
  name: string;
  color?: string;
  /** Drop rules only: "+" / "-" ("" clears on update). */
  emoji?: string;
  /** Drop + List rules: apply ONLY to single-sourced lists. */
  singleSourceOnly?: boolean;
  /** Drop rules only: system list tag gate ("New" / "Unused"); [] clears. */
  listSystemTags?: string[];
  conditions: TagRuleCondition[];
  dateRangePreset?: TagRuleDatePreset;
  fromDate?: string;
  toDate?: string;
  toDateIsToday?: boolean;
  /** Hour rules only (default 100). */
  minVolume?: number;
  enabled?: boolean;
  orderIndex?: number;
  groupKey?: string;
}

export interface UpdateListTagRuleDto {
  name?: string;
  color?: string;
  emoji?: string;
  singleSourceOnly?: boolean;
  listSystemTags?: string[];
  conditions?: TagRuleCondition[];
  dateRangePreset?: TagRuleDatePreset;
  fromDate?: string;
  toDate?: string;
  toDateIsToday?: boolean;
  minVolume?: number;
  enabled?: boolean;
  orderIndex?: number;
  groupKey?: string;
}

const listTagRulesKeys = {
  all: () => ['smswiser', 'list-tag-rules'] as const,
  settings: () => ['smswiser', 'list-tag-rules', 'settings'] as const,
};

export interface ListTagRulesSettings {
  newDays: number;
  newColor: string;
  unusedColor: string;
  /** ISO UTC; empty = all lists. When set, only lists with first_seen_at ≥ this get Unused (sends counted from each list first seen). */
  unusedSinceIso: string;
  cacheEnabled: boolean;
  cacheTtlHours: number;
}

export type UpdateListTagRulesSettingsPayload = Partial<{
  newDays: number;
  newColor: string;
  unusedColor: string;
  unusedSinceIso: string | null;
  cacheEnabled: boolean;
  cacheTtlHours: number;
}>;

export function useListTagRulesSettings() {
  return useQuery({
    queryKey: listTagRulesKeys.settings(),
    queryFn: async (): Promise<ListTagRulesSettings> => {
      const res = await api.get<{
        data?: {
          newDays?: number;
          newColor?: string;
          unusedColor?: string;
          unusedSinceIso?: string;
          cacheEnabled?: boolean;
          cacheTtlHours?: number;
        };
      }>('/smswiser/list-tag-rules/settings');
      const data =
        res.data?.data ??
        (res.data as {
          newDays?: number;
          newColor?: string;
          unusedColor?: string;
          unusedSinceIso?: string;
          cacheEnabled?: boolean;
          cacheTtlHours?: number;
        } | undefined);
      return {
        newDays: Number(data?.newDays) || 2,
        newColor: data?.newColor ?? '#3b82f6',
        unusedColor: data?.unusedColor ?? '#6b7280',
        unusedSinceIso: String(data?.unusedSinceIso ?? '').trim(),
        cacheEnabled: data?.cacheEnabled ?? true,
        cacheTtlHours: Math.max(1, Math.min(168, Number(data?.cacheTtlHours) || 12)),
      };
    },
  });
}

export function useUpdateListTagRulesSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      payload: UpdateListTagRulesSettingsPayload,
    ): Promise<ListTagRulesSettings> => {
      const res = await api.patch<{
        data?: {
          newDays?: number;
          newColor?: string;
          unusedColor?: string;
          unusedSinceIso?: string;
          cacheEnabled?: boolean;
          cacheTtlHours?: number;
        };
      }>('/smswiser/list-tag-rules/settings', payload);
      const data =
        res.data?.data ??
        (res.data as {
          newDays?: number;
          newColor?: string;
          unusedColor?: string;
          unusedSinceIso?: string;
          cacheEnabled?: boolean;
          cacheTtlHours?: number;
        } | undefined);
      return {
        newDays: Number(data?.newDays) || 2,
        newColor: data?.newColor ?? '#3b82f6',
        unusedColor: data?.unusedColor ?? '#6b7280',
        unusedSinceIso: String(data?.unusedSinceIso ?? '').trim(),
        cacheEnabled: data?.cacheEnabled ?? true,
        cacheTtlHours: Math.max(1, Math.min(168, Number(data?.cacheTtlHours) || 12)),
      };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: listTagRulesKeys.settings() });
      invalidateListPerformanceTags(queryClient);
    },
  });
}

export function useListTagRules() {
  return useQuery({
    queryKey: listTagRulesKeys.all(),
    queryFn: async () => {
      const res = await api.get<{ data: ListTagRule[] }>('/smswiser/list-tag-rules');
      const data = res.data?.data;
      return Array.isArray(data) ? data : [];
    },
  });
}

/** Invalidate list-performance-tags so Creator/Scale pickers reflect tag color changes immediately. */
function invalidateListPerformanceTags(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({
    predicate: (query) =>
      Array.isArray(query.queryKey) && query.queryKey.includes('list-performance-tags'),
  });
}

export function useCreateListTagRule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (dto: CreateListTagRuleDto) => {
      const res = await api.post<{ data: ListTagRule }>('/smswiser/list-tag-rules', dto);
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: listTagRulesKeys.all() });
      invalidateListPerformanceTags(queryClient);
    },
  });
}

export function useBulkCreateListTagRules() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (rules: CreateListTagRuleDto[]) => {
      const res = await api.post<{ data: ListTagRule[]; created: number }>(
        '/smswiser/list-tag-rules/bulk',
        { rules },
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: listTagRulesKeys.all() });
      invalidateListPerformanceTags(queryClient);
    },
  });
}

export function useUpdateListTagRule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, dto }: { id: string; dto: UpdateListTagRuleDto }) => {
      const res = await api.patch<{ data: ListTagRule }>(`/smswiser/list-tag-rules/${id}`, dto);
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: listTagRulesKeys.all() });
      invalidateListPerformanceTags(queryClient);
    },
  });
}

export function useDeleteListTagRule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/smswiser/list-tag-rules/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: listTagRulesKeys.all() });
      invalidateListPerformanceTags(queryClient);
    },
  });
}

export function useReorderListTagRules() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (updates: Array<{ id: string; orderIndex: number }>) => {
      const res = await api.patch<{ data: { updated: number } }>(
        '/smswiser/list-tag-rules/reorder',
        { updates },
      );
      return res.data?.data ?? { updated: 0 };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: listTagRulesKeys.all() });
      invalidateListPerformanceTags(queryClient);
    },
  });
}
